import { Component, ChangeDetectionStrategy, computed, input, signal } from '@angular/core';
import { compact } from '../../../core/dataviz';

/** Une catégorie de l'axe horizontal (jour, mois, tranche…). */
export interface GroupeColonnes { cle: string; label: string; sousLabel?: string; }

/**
 * Une série. `negatif` la trace SOUS la ligne de zéro (sorties) ; `couleurs` donne une
 * couleur par colonne quand la catégorie elle-même porte le sens (tranches de retard).
 */
export interface SerieColonnes {
  nom: string;
  couleur: string;
  valeurs: number[];
  negatif?: boolean;
  couleurs?: string[];
}

/**
 * Colonnes verticales — groupées, ou de part et d'autre de zéro (entrées / sorties).
 *
 * Une seule échelle (jamais deux axes). Marques fines : bout arrondi 4 px côté valeur,
 * collé à la ligne de base, 2 px de fond entre colonnes voisines. Survol d'une catégorie :
 * infobulle avec chaque série et, si fourni, le net (point foncé).
 */
@Component({
  selector: 'app-colonnes',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="select-none">
      <div class="relative" [style.height.px]="hauteur()">
        <!-- Graduations : discrètes, en retrait des données -->
        @for (g of graduations(); track g.top) {
          <div class="absolute left-10 right-0 border-t border-stone-100" [style.top.%]="g.top"></div>
          <span class="absolute left-0 w-9 -translate-y-1/2 text-right text-[10px] leading-none text-stone-400 tabular-nums"
                [style.top.%]="g.top">{{ g.label }}</span>
        }
        <div class="absolute left-10 right-0 border-t border-stone-300" [style.top.%]="zero()"></div>

        <div class="absolute inset-y-0 left-10 right-0 flex">
          @for (g of groupes(); track g.cle; let i = $index) {
            <div class="relative flex-1 h-full cursor-default" (mouseenter)="survol.set(i)" (mouseleave)="survol.set(null)">
              @if (survol() === i) {
                <div class="absolute inset-y-0 inset-x-px bg-stone-100/70 rounded"></div>
              }
              <!-- Au-dessus de zéro -->
              <div class="absolute inset-x-0 top-0 flex items-end justify-center gap-[2px] px-[1px]" [style.height.%]="zero()">
                @for (s of positives(); track s.nom) {
                  <div class="flex-1 rounded-t-[4px]" [style.max-width.px]="largeurMax()"
                       [style.height.%]="pct(s.valeurs[i], maxPos())"
                       [style.min-height.px]="s.valeurs[i] > 0 ? 2 : 0"
                       [style.background-color]="s.couleurs?.[i] ?? s.couleur"></div>
                }
              </div>
              <!-- Sous zéro -->
              @if (negatives().length) {
                <div class="absolute inset-x-0 bottom-0 flex items-start justify-center gap-[2px] px-[1px]" [style.height.%]="100 - zero()">
                  @for (s of negatives(); track s.nom) {
                    <div class="flex-1 rounded-b-[4px]" [style.max-width.px]="largeurMax()"
                         [style.height.%]="pct(s.valeurs[i], maxNeg())"
                         [style.min-height.px]="s.valeurs[i] > 0 ? 2 : 0"
                         [style.background-color]="s.couleurs?.[i] ?? s.couleur"></div>
                  }
                </div>
              }
              @if (net(); as n) {
                @if (n[i] !== 0) {
                  <div class="absolute left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white"
                       [style.top.%]="position(n[i])" [style.background-color]="couleurNet"></div>
                }
              }
              @if (etiquettes() && total(i) > 0) {
                <span class="absolute inset-x-0 -translate-y-full pb-0.5 text-center text-[10px] font-medium text-stone-600 tabular-nums whitespace-nowrap"
                      [style.top.%]="position(sommePositive(i))">{{ total(i) | number:'1.0-0' }}</span>
              }
            </div>
          }
        </div>

        <!-- Infobulle -->
        @if (survol() !== null) {
          <div class="absolute z-10 -top-2 pointer-events-none bg-white border border-stone-200 shadow-lg rounded-xl px-3 py-2 text-xs min-w-[150px]"
               [style.left]="'calc(2.5rem + (100% - 2.5rem) * ' + ancrage() + ')'"
               [style.transform]="'translate(-' + (ancrage() * 100) + '%, -100%)'">
            <p class="font-semibold text-stone-700 mb-1 whitespace-nowrap">{{ titreSurvol() }}</p>
            @for (s of series(); track s.nom) {
              <p class="flex items-center gap-2 whitespace-nowrap text-stone-600">
                <span class="w-2 h-2 rounded-sm flex-shrink-0" [style.background-color]="s.couleurs?.[survol()!] ?? s.couleur"></span>
                <span class="flex-1">{{ s.nom }}</span>
                <span class="font-medium text-stone-800 tabular-nums">{{ s.valeurs[survol()!] | number:'1.2-2' }}</span>
              </p>
            }
            @if (net(); as n) {
              <p class="flex items-center gap-2 whitespace-nowrap text-stone-600 border-t border-stone-100 mt-1 pt-1">
                <span class="w-2 h-2 rounded-full flex-shrink-0" [style.background-color]="couleurNet"></span>
                <span class="flex-1">{{ nomNet() }}</span>
                <span class="font-semibold text-stone-800 tabular-nums">{{ n[survol()!] | number:'1.2-2' }}</span>
              </p>
            }
          </div>
        }
      </div>

      <!-- Axe des catégories : une étiquette sur « pas » quand elles sont nombreuses -->
      <div class="flex pl-10 mt-1.5">
        @for (g of groupes(); track g.cle; let i = $index) {
          <div class="flex-1 min-w-0 text-center">
            @if (i % pas() === 0) {
              <p class="text-[10px] leading-tight text-stone-500 whitespace-nowrap">{{ g.label }}</p>
              @if (g.sousLabel) {
                <p class="text-[10px] leading-tight text-stone-400 truncate">{{ g.sousLabel }}</p>
              }
            }
          </div>
        }
      </div>
    </div>
  `,
})
export class ColonnesComponent {
  groupes = input.required<GroupeColonnes[]>();
  series = input.required<SerieColonnes[]>();
  /** Valeur nette par catégorie (point), sur la même échelle. */
  net = input<number[] | null>(null);
  nomNet = input('Net');
  /** Valeur au-dessus de chaque colonne — seulement quand les catégories sont peu nombreuses. */
  etiquettes = input(false);
  hauteur = input(200);

  readonly couleurNet = '#292524';
  survol = signal<number | null>(null);

  positives = computed(() => this.series().filter(s => !s.negatif));
  negatives = computed(() => this.series().filter(s => s.negatif));

  private maxDe = (series: SerieColonnes[]) =>
    Math.max(0, ...series.flatMap(s => s.valeurs));
  maxPos = computed(() => this.maxDe(this.positives()));
  maxNeg = computed(() => this.maxDe(this.negatives()));

  /** Position (en % de la hauteur) de la ligne de zéro. */
  zero = computed(() => {
    const p = this.maxPos();
    const n = this.maxNeg();
    if (n <= 0) return 100;
    if (p <= 0) return 0;
    return (p / (p + n)) * 100;
  });

  /** Colonnes plus étroites quand il y a peu de catégories — jamais un pavé. */
  largeurMax = computed(() => (this.groupes().length <= 6 ? 44 : 22));

  pas = computed(() => Math.max(1, Math.ceil(this.groupes().length / 10)));

  graduations = computed(() => {
    const z = this.zero();
    const g: { top: number; label: string }[] = [];
    if (this.maxPos() > 0) {
      g.push({ top: 0, label: compact(this.maxPos()) });
      g.push({ top: z / 2, label: compact(this.maxPos() / 2) });
    }
    if (this.maxNeg() > 0) {
      g.push({ top: 100, label: compact(-this.maxNeg()) });
    }
    return g;
  });

  pct(v: number, max: number): number {
    return max > 0 ? Math.max(0, (v / max) * 100) : 0;
  }

  /** Position verticale (en %) d'une valeur signée sur l'échelle unique. */
  position(v: number): number {
    const z = this.zero();
    if (v >= 0) return this.maxPos() > 0 ? z * (1 - v / this.maxPos()) : z;
    return this.maxNeg() > 0 ? z + (100 - z) * (Math.abs(v) / this.maxNeg()) : z;
  }

  sommePositive(i: number): number {
    return Math.max(0, ...this.positives().map(s => s.valeurs[i] ?? 0));
  }

  total(i: number): number {
    return this.positives().reduce((t, s) => t + (s.valeurs[i] ?? 0), 0);
  }

  /** Ancrage horizontal de l'infobulle (0 à 1), retenu près des bords pour rester visible. */
  ancrage = computed(() => {
    const i = this.survol();
    const n = this.groupes().length;
    if (i === null || n === 0) return 0.5;
    return Math.min(0.85, Math.max(0.15, (i + 0.5) / n));
  });

  titreSurvol = computed(() => {
    const i = this.survol();
    if (i === null) return '';
    const g = this.groupes()[i];
    return g.sousLabel ? `${g.label} — ${g.sousLabel}` : g.label;
  });
}
